import React, { useState, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { colors, fontStyles, spacing } from '../../../../styles';
import { TIMING, blink, typingDots } from '../constants';

// AI model provider logos
import claudeIcon from '../../../../assets/Icons/claude.svg';
import openaiIcon from '../../../../assets/Icons/openai.svg';
import geminiIcon from '../../../../assets/Icons/gemini.svg';

interface AIChatAnimationProps {
  isActive: boolean;
}

const USER_QUESTION = 'Is NVDA a good buy right now?';
const AI_RESPONSE = `Based on current valuations, NVDA trades at 65x P/E vs sector avg of 25x. However, with +122% YoY growth...`;

export const AIChatAnimation: React.FC<AIChatAnimationProps> = ({ isActive }) => {
  const [phase, setPhase] = useState<'idle' | 'typing-question' | 'thinking' | 'streaming'>('idle');
  const [questionText, setQuestionText] = useState('');
  const [responseText, setResponseText] = useState('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const charIndexRef = useRef(0);
  const hasStartedRef = useRef(false);

  // Reset animation when becoming active
  useEffect(() => {
    if (isActive && !hasStartedRef.current) {
      hasStartedRef.current = true;
      setPhase('idle');
      setQuestionText('');
      setResponseText('');
      charIndexRef.current = 0;

      // Start typing after a brief delay
      timeoutRef.current = setTimeout(() => {
        setPhase('typing-question');
      }, 300);
    } else if (!isActive) {
      // Reset the started flag when becoming inactive
      hasStartedRef.current = false;
      setPhase('idle');
      setQuestionText('');
      setResponseText('');
      charIndexRef.current = 0;
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isActive]);

  // Typewriter effect for question
  useEffect(() => {
    if (phase !== 'typing-question') return;

    const typeNextChar = () => {
      if (charIndexRef.current < USER_QUESTION.length) {
        setQuestionText(USER_QUESTION.substring(0, charIndexRef.current + 1));
        charIndexRef.current += 1;
        const delay = TIMING.typewriterCharDelay + Math.random() * TIMING.typewriterJitter;
        timeoutRef.current = setTimeout(typeNextChar, delay);
      } else {
        // Question complete, show thinking
        timeoutRef.current = setTimeout(() => {
          setPhase('thinking');
          charIndexRef.current = 0;
        }, 300);
      }
    };

    timeoutRef.current = setTimeout(typeNextChar, 200);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [phase]);

  // Thinking phase then streaming
  useEffect(() => {
    if (phase !== 'thinking') return;

    timeoutRef.current = setTimeout(() => {
      setPhase('streaming');
    }, 800);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [phase]);

  // Streaming effect for response
  useEffect(() => {
    if (phase !== 'streaming') return;

    const streamNextChar = () => {
      if (charIndexRef.current < AI_RESPONSE.length) {
        setResponseText(AI_RESPONSE.substring(0, charIndexRef.current + 1));
        charIndexRef.current += 1;
        // Faster streaming for AI response
        const delay = 15 + Math.random() * 10;
        timeoutRef.current = setTimeout(streamNextChar, delay);
      }
    };

    timeoutRef.current = setTimeout(streamNextChar, 100);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [phase]);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.centi};
        padding: ${spacing.deci};
        height: 100%;
        overflow: hidden;
      `}
    >
      {/* User message bubble */}
      {(phase === 'typing-question' || phase === 'thinking' || phase === 'streaming') && questionText && (
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
          `}
        >
          <div
            css={css`
              background: ${colors.petrolBlue};
              color: ${colors.sugar};
              padding: ${spacing.centi} ${spacing.deci};
              border-radius: 16px 16px 4px 16px;
              max-width: 80%;
              ${fontStyles.cat};

              html[data-theme='dark'] & {
                background: ${colors.skyBlue};
                color: ${colors.liquorice};
              }
            `}
          >
            {questionText}
            {phase === 'typing-question' && (
              <span
                css={css`
                  animation: ${blink} 1s step-end infinite;
                  margin-left: 2px;
                `}
              >
                |
              </span>
            )}
          </div>
        </div>
      )}

      {/* Typing indicator */}
      {phase === 'thinking' && (
        <div
          css={css`
            display: flex;
            gap: 4px;
            padding: ${spacing.centi};
          `}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              css={css`
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background: ${colors.tungsten};
                animation: ${typingDots} 1.4s ease-in-out infinite;
                animation-delay: ${i * 0.2}s;

                html[data-theme='dark'] & {
                  background: ${colors.silver};
                }
              `}
            />
          ))}
        </div>
      )}

      {/* AI response bubble */}
      {phase === 'streaming' && responseText && (
        <div
          css={css`
            display: flex;
            justify-content: flex-start;
          `}
        >
          <div
            css={css`
              background: ${colors.sugar};
              color: ${colors.liquorice};
              padding: ${spacing.deci};
              border-radius: 16px 16px 16px 4px;
              max-width: 90%;
              ${fontStyles.cat};
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

              html[data-theme='dark'] & {
                background: ${colors.liquorice};
                color: ${colors.sugar};
              }
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: flex-start;
                gap: ${spacing.centi};
              `}
            >
              <span>🤖</span>
              <div>
                {responseText}
                {responseText.length < AI_RESPONSE.length && (
                  <span
                    css={css`
                      animation: ${blink} 1s step-end infinite;
                      margin-left: 2px;
                      color: ${colors.petrolBlue};

                      html[data-theme='dark'] & {
                        color: ${colors.skyBlue};
                      }
                    `}
                  >
                    ▎
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Model provider logos */}
      <div
        css={css`
          margin-top: auto;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: ${spacing.centi};
        `}
      >
        <span
          css={css`
            ${fontStyles.mouse};
            color: ${colors.tungsten};

            html[data-theme='dark'] & {
              color: ${colors.silver};
            }
          `}
        >
          Powered by
        </span>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing.centi};
          `}
        >
          <img
            src={claudeIcon}
            alt="Claude"
            css={css`
              width: 20px;
              height: 20px;
            `}
          />
          <img
            src={openaiIcon}
            alt="OpenAI"
            css={css`
              width: 20px;
              height: 20px;
              html[data-theme='dark'] & {
                filter: invert(1);
              }
            `}
          />
          <img
            src={geminiIcon}
            alt="Gemini"
            css={css`
              width: 20px;
              height: 20px;
            `}
          />
        </div>
      </div>

      {/* Input placeholder */}
      <div
        css={css`
          background: ${colors.sugar};
          border: 1px solid ${colors.cloud};
          border-radius: 20px;
          padding: ${spacing.centi} ${spacing.deci};
          ${fontStyles.cat};
          color: ${colors.cloud};

          html[data-theme='dark'] & {
            background: ${colors.liquorice};
            border-color: ${colors.tungsten};
            color: ${colors.sugar};
          }
        `}
      >
        Type a question...
      </div>
    </div>
  );
};
